import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { format } from 'date-fns';

const categoryLabels = { L: 'Lift', R: 'SAQ+C', C: 'Circuit' };
const genderLabels = {
  0: 'All', 1: 'Male', 2: 'Female', 3: 'Undefined',
};
const rangeTypeLabels = {
  1: 'All Time', 2: 'Last 30 Days', 3: 'Today', 4: 'Custom', 5: 'Current Max',
};
const compareToLabels = {
  1: 'All Time', 2: 'Last 30 Days', 3: 'Today', 4: 'Custom', 5: 'Current Max',
};

const formatDateRange = (dates) => {
  if (!dates || !dates.startDate || !dates.endDate) return 'Custom';
  return `${format(new Date(dates.startDate), 'MMMM d, yyyy')} – ${format(new Date(dates.endDate), 'MMMM d, yyyy')}`;
};

const BASE_ROW_FONT_SIZE = 13; // 1em reference for the scale cascade below

const TableContainer = styled('div')`
  font-family: 'Nunito Sans';
`;

const SummaryRow = styled('div')`
  display: flex;
  color: #444;
  margin-bottom: 0.615em;

  &:last-child {
    margin-bottom: 0;
  }

  span:first-of-type {
    font-weight: 700;
    width: 8.462em;
    flex-shrink: 0;
    color: #222;
  }

  span:last-of-type {
    flex: 1;
    min-width: 0;
    word-break: break-word;
  }
`;

const LeaderboardSummaryTable = ({
  category,
  exerciseName,
  groups,
  mustBeInAllGroups,
  gender,
  rangeType,
  rangeDates,
  compareTo,
  compareDates,
  scaleFactor,
}) => {
  const timeFrameValue = rangeType === 4
    ? formatDateRange(rangeDates)
    : (rangeTypeLabels[rangeType] || '—');

  let compareToValue = 'N/A';
  if (compareTo === 4) compareToValue = formatDateRange(compareDates);
  else if (compareTo) compareToValue = compareToLabels[compareTo];

  const rows = [
    { label: 'Category', value: categoryLabels[category] || category },
    { label: 'Exercise', value: exerciseName },
    { label: 'Groups', value: (groups || []).map((g) => g.name).join(', ') || '—' },
    { label: 'Athletes', value: mustBeInAllGroups ? 'Must be in all selected groups' : 'Can be in any selected group' },
    { label: 'Gender', value: genderLabels[gender] ?? 'All' },
    { label: 'Time Frame', value: timeFrameValue },
    { label: 'Compare To', value: compareToValue },
  ];

  return (
    <TableContainer style={{ fontSize: `${Math.round(BASE_ROW_FONT_SIZE * scaleFactor)}px` }}>
      {rows.map(({ label, value }) => (
        <SummaryRow key={label}>
          <span>{`${label}:`}</span>
          <span>{value}</span>
        </SummaryRow>
      ))}
    </TableContainer>
  );
};

LeaderboardSummaryTable.defaultProps = {
  groups: [],
  mustBeInAllGroups: false,
  gender: 0,
  rangeDates: {},
  compareDates: {},
  compareTo: 0,
  scaleFactor: 1,
};

LeaderboardSummaryTable.propTypes = {
  category: PropTypes.string.isRequired,
  exerciseName: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  mustBeInAllGroups: PropTypes.bool,
  gender: PropTypes.number,
  rangeType: PropTypes.number.isRequired,
  rangeDates: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }),
  compareTo: PropTypes.number,
  compareDates: PropTypes.shape({
    startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }),
  scaleFactor: PropTypes.number,
};

export default LeaderboardSummaryTable;
